/**
 * Validation utilities for lessons, slides, and answers
 * Integrates with plugin system for slide-specific validation
 */

import { validateLesson } from '../config/lesson-types.config';
import { slideRegistry } from '../core/plugin-system';
import type { LessonDTO, SlideDTO, AnswerDTO } from '../core/types/lesson.types';
import type {
  LessonValidationResult,
  SlideValidationResult,
  AnswerValidationResult,
} from '../core/types/workflow.types';

// ============================================================================
// LESSON VALIDATION
// ============================================================================

/**
 * Validates an entire lesson including all slides
 * Uses both lesson type configuration and plugin validators
 *
 * @param lesson - The lesson to validate
 * @returns Validation result with errors grouped by field
 */
export function validateLessonComplete(
  lesson: LessonDTO
): LessonValidationResult {
  const errors: Record<string, string[]> = {};
  const warnings: Record<string, string[]> = {};

  // 1. Validate lesson metadata using config
  const metadataValidation = validateLesson(lesson);
  if (!metadataValidation.valid) {
    errors['_lesson'] = metadataValidation.errors;
  }

  // 2. Validate each slide using plugins
  if (lesson.slides && lesson.slides.length > 0) {
    lesson.slides.forEach((slide, index) => {
      const slideValidation = validateSlideWithPlugin(slide);

      if (!slideValidation.valid) {
        errors[`slides[${index}]`] = slideValidation.errors;
      }

      if (slideValidation.warnings.length > 0) {
        warnings[`slides[${index}]`] = slideValidation.warnings;
      }
    });
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
    warnings,
  };
}

/**
 * Validates lesson metadata only (without slides)
 * Useful for quick validation during editing
 *
 * @param lesson - The lesson to validate
 * @returns Validation result
 */
export function validateLessonMetadata(lesson: LessonDTO): {
  valid: boolean;
  errors: string[];
} {
  return validateLesson(lesson);
}

// ============================================================================
// SLIDE VALIDATION
// ============================================================================

/**
 * Validates a single slide using its plugin validator
 *
 * @param slide - The slide to validate
 * @returns Validation result with slide-specific errors
 */
export function validateSlideWithPlugin(slide: SlideDTO): SlideValidationResult {
  if (!slide.type) {
    return {
      slideId: slide.id!,
      slideIndex: -1,
      valid: false,
      errors: ['Tipul de slide nu a fost specificat'],
      warnings: [],
    };
  }

  const plugin = slideRegistry.get(slide.type);

  if (!plugin) {
    return {
      slideId: slide.id!,
      slideIndex: -1,
      valid: false,
      errors: [`Plugin pentru tipul de slide "${slide.type}" nu a fost găsit`],
      warnings: [],
    };
  }

  // Use plugin's creation validator
  const validation = plugin.validateCreation(slide);

  return {
    slideId: slide.id!,
    slideIndex: -1, // Will be filled by caller if needed
    valid: validation.valid,
    errors: validation.errors,
    warnings: validation.warnings || [],
  };
}

/**
 * Validates multiple slides in batch
 *
 * @param slides - Array of slides to validate
 * @returns Array of validation results
 */
export function validateSlidesBatch(slides: SlideDTO[]): SlideValidationResult[] {
  return slides.map((slide, index) => {
    const result = validateSlideWithPlugin(slide);
    return {
      ...result,
      slideIndex: index,
    };
  });
}

/**
 * Checks if all slides in a lesson are valid
 *
 * @param slides - Array of slides
 * @returns True if all slides are valid
 */
export function areAllSlidesValid(slides: SlideDTO[]): boolean {
  const results = validateSlidesBatch(slides);
  return results.every((result) => result.valid);
}

// ============================================================================
// ANSWER VALIDATION
// ============================================================================

/**
 * Validates an answer for a specific slide
 * Uses the slide's plugin validator
 *
 * @param answer - The answer to validate
 * @param slide - The slide being answered
 * @returns Validation result
 */
export function validateAnswer(
  answer: Partial<AnswerDTO>,
  slide: SlideDTO
): AnswerValidationResult {
  if (!slide.type) {
    return {
      valid: false,
      errors: ['Tipul de slide nu a fost specificat'],
      canSubmit: false,
    };
  }

  const plugin = slideRegistry.get(slide.type);

  if (!plugin) {
    return {
      valid: false,
      errors: [`Plugin pentru tipul de slide "${slide.type}" nu a fost găsit`],
      canSubmit: false,
    };
  }

  // Use plugin's answer validator
  const validation = plugin.validateAnswer(answer, slide);

  return {
    valid: validation.valid,
    errors: validation.errors,
    canSubmit: validation.valid, // Can only submit if valid
  };
}

/**
 * Validates answers for all slides in a lesson
 * Used when submitting entire lesson
 *
 * @param answers - Array of answers
 * @param lesson - The lesson containing the slides
 * @returns Map of slide ID to validation result
 */
export function validateAllAnswers(
  answers: Partial<AnswerDTO>[],
  lesson: LessonDTO
): Map<string, AnswerValidationResult> {
  const results = new Map<string, AnswerValidationResult>();

  answers.forEach((answer) => {
    if (!answer.slideId) {
      return;
    }

    const slide = lesson.slides?.find((s) => s.id === answer.slideId);
    if (!slide) {
      results.set(answer.slideId, {
        valid: false,
        errors: ['Slide-ul nu a fost găsit'],
        canSubmit: false,
      });
      return;
    }

    results.set(answer.slideId, validateAnswer(answer, slide));
  });

  return results;
}

/**
 * Checks if student can submit the lesson
 * All required slides must have valid answers
 *
 * @param answers - Student's answers
 * @param lesson - The lesson
 * @returns True if can submit
 */
export function canSubmitLesson(
  answers: Partial<AnswerDTO>[],
  lesson: LessonDTO
): boolean {
  const validationResults = validateAllAnswers(answers, lesson);

  // Check if all slides have been answered with valid answers
  const allSlidesAnswered = lesson.slides?.every((slide) => {
    const result = validationResults.get(slide.id!);
    return result && result.valid;
  });

  return allSlidesAnswered || false;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Formats validation errors into user-friendly messages
 *
 * @param errors - Record of field errors
 * @returns Flat array of formatted error messages
 */
export function formatValidationErrors(
  errors: Record<string, string[]>
): string[] {
  const formatted: string[] = [];

  Object.entries(errors).forEach(([field, fieldErrors]) => {
    // Format field name (e.g., "slides[0]" -> "Slide 1")
    let fieldLabel = field;
    if (field.startsWith('slides[')) {
      const index = parseInt(field.match(/\d+/)?.[0] || '0');
      fieldLabel = `Slide ${index + 1}`;
    } else if (field === '_lesson') {
      fieldLabel = 'Lecție';
    }

    fieldErrors.forEach((error) => {
      formatted.push(`${fieldLabel}: ${error}`);
    });
  });

  return formatted;
}

/**
 * Groups validation errors by severity
 *
 * @param validation - Validation result
 * @returns Grouped errors
 */
export function groupValidationErrors(validation: LessonValidationResult): {
  critical: string[];
  warnings: string[];
  info: string[];
} {
  const critical: string[] = [];
  const warnings: string[] = [];
  const info: string[] = [];

  // Critical errors prevent saving
  Object.entries(validation.errors).forEach(([field, errors]) => {
    if (field === '_lesson' || field.startsWith('slides')) {
      critical.push(...errors);
    }
  });

  // Warnings don't prevent saving but should be shown
  Object.entries(validation.warnings).forEach(([_, warningMessages]) => {
    warnings.push(...warningMessages);
  });

  return { critical, warnings, info };
}

/**
 * Checks if validation has any blocking errors
 *
 * @param validation - Validation result
 * @returns True if there are errors that prevent saving
 */
export function hasBlockingErrors(validation: LessonValidationResult): boolean {
  return !validation.valid;
}

/**
 * Gets validation summary message
 *
 * @param validation - Validation result
 * @returns Human-readable summary
 */
export function getValidationSummary(validation: LessonValidationResult): string {
  if (validation.valid) {
    return 'Lecția este validă și poate fi salvată';
  }

  const errorCount = Object.keys(validation.errors).length;
  const warningCount = Object.keys(validation.warnings).length;

  const parts: string[] = [];

  if (errorCount > 0) {
    parts.push(`${errorCount} erori`);
  }

  if (warningCount > 0) {
    parts.push(`${warningCount} avertismente`);
  }

  return `Lecția conține ${parts.join(' și ')}`;
}
